/**
 * ProgressBar Component
 * 
 * A reusable progress bar component for displaying budget health, completion percentages, etc.
 * Supports different color variants based on progress level (green for good, red for over, blue for in-progress)
 * 
 * Design Principles:
 * - Clean visual representation of progress
 * - Color-coded based on percentage (green < 80%, yellow 80-95%, red > 95%)
 * - Shows both visual bar and text information
 * - Accessible with ARIA attributes
 */

import React from 'react';
import './ProgressBar.css';

export interface ProgressBarProps {
  /** Current value (e.g., amount spent) */
  current: number;
  /** Maximum value (e.g., total budget) */
  total: number;
  /** Display format: 'percentage' shows %, 'amount' shows $X of $Y, 'both' shows both */
  displayFormat?: 'percentage' | 'amount' | 'both';
  /** Custom color (overrides auto color based on percentage) */
  color?: string;
  /** Size variant */
  size?: 'small' | 'medium' | 'large';
  /** Additional CSS class name */
  className?: string;
  /** Show label above the bar */
  showLabel?: boolean;
  /** Custom label text */
  label?: string;
}

/**
 * Determines color based on percentage
 * - Green: < 80% (good)
 * - Yellow/Amber: 80-95% (warning)
 * - Red: > 95% (critical)
 */
const getProgressColor = (percentage: number, customColor?: string): string => {
  if (customColor) return customColor;
  
  if (percentage >= 95) return '#dc3545'; // Red - critical
  if (percentage >= 80) return '#ffc107'; // Yellow/Amber - warning
  return '#28a745'; // Green - good
};

/**
 * Formats the display text based on the format prop
 */
const formatDisplayText = (
  current: number,
  total: number,
  format: 'percentage' | 'amount' | 'both'
): string => {
  const percentage = total > 0 ? Math.round((current / total) * 100) : 0;
  
  switch (format) {
    case 'percentage':
      return `${percentage}%`;
    case 'amount':
      return `$${formatNumber(current)}K of $${formatNumber(total)}K`;
    case 'both':
      return `$${formatNumber(current)}K of $${formatNumber(total)}K (${percentage}%)`;
    default:
      return `${percentage}%`;
  }
};

/**
 * Formats numbers for display (converts to K notation if > 1000)
 */
const formatNumber = (num: number): string => {
  if (num >= 1000) {
    return (num / 1000).toFixed(0);
  }
  return num.toFixed(0);
};

const ProgressBar: React.FC<ProgressBarProps> = ({
  current,
  total,
  displayFormat = 'amount',
  color,
  size = 'medium',
  className = '',
  showLabel = false,
  label,
}) => {
  const percentage = total > 0 ? Math.min(Math.round((current / total) * 100), 100) : 0;
  const progressColor = getProgressColor(percentage, color);
  const displayText = formatDisplayText(current, total, displayFormat);

  return (
    <div className={`progress-bar-container progress-bar-${size} ${className}`}>
      {showLabel && (
        <div className="progress-bar-label">
          {label || 'Progress'}
        </div>
      )}
      <div className="progress-bar-wrapper">
        <div
          className="progress-bar-fill"
          style={{
            width: `${percentage}%`,
            backgroundColor: progressColor,
          }}
          role="progressbar"
          aria-valuenow={percentage}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${percentage}% complete`}
        />
      </div>
      <div className="progress-bar-text">{displayText}</div>
    </div>
  );
};

export default ProgressBar;

